import {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useRef,
} from "react";

/**
 * Hover handlers that mark a reference as focused in its task ref context, so
 * the matching table row highlights and scrolls into view. The focus is
 * cleared on mouse leave and, if this chip is still hovered, on unmount.
 */
export function useRefChipHoverFocus<TId extends string | number>({
    id,
    setFocusedId,
}: {
    id: TId;
    setFocusedId?: Dispatch<SetStateAction<TId | null>>;
}) {
    const isHoveredRef = useRef(false);

    const handleMouseEnter = useCallback(() => {
        isHoveredRef.current = true;
        setFocusedId?.(id);
    }, [setFocusedId, id]);

    const handleMouseLeave = useCallback(() => {
        isHoveredRef.current = false;
        setFocusedId?.((current) => (current === id ? null : current));
    }, [setFocusedId, id]);

    useEffect(
        () => () => {
            if (!isHoveredRef.current) {
                return;
            }
            isHoveredRef.current = false;
            setFocusedId?.((current) => (current === id ? null : current));
        },
        [setFocusedId, id],
    );

    return { handleMouseEnter, handleMouseLeave };
}
